// File: clientapp/src/components/DocHistoryPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
    Button,
    CircularProgress,
    Box,
    Typography,
    Snackbar,
    Alert
} from '@mui/material';
import PageLayout from './PageLayout';

export default function DocHistoryPage() {
    const navigate = useNavigate();
    const [uploads, setUploads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [uploadFilter, setUploadFilter] = useState("my"); // "my" or "all"

    const fetchHistory = useCallback(async () => {
        setLoading(true);
        try {
            // Build query parameters; assume server expects page to be 1-indexed.
            const params = new URLSearchParams({
                page: page + 1,
                pageSize: rowsPerPage,
                search: searchTerm,
                filter: uploadFilter
            });
            const response = await fetch(`/api/classification/history?${params.toString()}`, { credentials: 'include' });
            if (!response.ok) {
                const errText = await response.text();
                throw new Error(errText || "Failed to fetch history.");
            }
            const data = await response.json();
            setUploads(data.items);
            setTotalRows(data.total);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, searchTerm, uploadFilter]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const handleFilterChange = (event) => {
        setUploadFilter(event.target.value);
        setPage(0);
    };

    const handleViewDetails = (id) => {
        navigate(`/classification-results/${id}`);
    };

    return (
        <PageLayout
            title="Document History"
            subTitle="Review past classified documents."
            breadcrumb={[
                { label: "Home", to: "/" },
                { label: "Documents", to: "/documents" },
                { label: "Document History" }
            ]}
        >
            <Paper sx={{ p: 2, mb: 2 }}>
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <RadioGroup row value={uploadFilter} onChange={handleFilterChange}>
                        <FormControlLabel value="my" control={<Radio />} label="My Uploads" />
                        <FormControlLabel value="all" control={<Radio />} label="All Uploads" />
                    </RadioGroup>
                </FormControl>
                <TextField
                    label="Search"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    fullWidth
                    sx={{ mb: 2 }}
                />
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 200 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Typography color="error">{error}</Typography>
                ) : (
                    <>
                        <TableContainer sx={{ maxHeight: '60vh' }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>File Name</TableCell>
                                        <TableCell>Uploaded At</TableCell>
                                        <TableCell>Uploaded By</TableCell>
                                        <TableCell>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {uploads.map((upload) => (
                                        <TableRow key={upload.id}>
                                            <TableCell>{upload.id}</TableCell>
                                            <TableCell>{upload.fileName}</TableCell>
                                            <TableCell>{new Date(upload.uploadedAt).toLocaleString()}</TableCell>
                                            <TableCell>
                                                {upload.uploadedBy || '—'}             
                                            </TableCell>
                                            <TableCell>
                                                <Button variant="outlined" onClick={() => handleViewDetails(upload.id)}>
                                                    View Details
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={totalRows}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25]}
                        />
                    </>
                )}
            </Paper>
            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError("")}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setError("")} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </PageLayout>
    );
}
